import { useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearPDPASnooze,
  isPDPASnoozed,
  needsPDPAConsent,
  snoozeKey,
  snoozePDPA,
} from "../../../helpers/pdpa";
import Color from "../../../assets/colors";
import {
  checkPDPAStatus,
  updatePDPAStatus,
} from "../../../redux/actions/user_action";
import * as types from "../../../redux/types/user_type";
import {
  BoxContainer,
  ButtonStyled,
  CloseButton,
  CloseIcon,
  DialogContainer,
  HiredlyIcon,
  LinkStyled,
  PrivacyIcon,
  TextStyled,
} from "./styles";

function SharedPrivacyDialog() {
  const dispatch = useDispatch();
  const [openCheckDialog, setOpenCheckDialog] = useState(false);
  const router = useRouter();
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);

  const isMobile = useMediaQuery("(max-width:640px)");
  const requested = useSelector((state) => state.user.pdpaDialogRequested);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [dismissal, setDismissal] = useState(null);
  const [storageRevision, setStorageRevision] = useState(0);
  const identity = useRef(user?.id);
  identity.current = user?.id;
  const submitting = useRef(false);

  async function refreshStatus() {
    const userId = user?.id;
    const result = await dispatch(checkPDPAStatus());
    if (identity.current === userId) setStatusError(!result);
  }

  // Identity arrival covers email/social login. Route and application triggers
  // refresh the same status without delaying navigation or application submission.
  useEffect(() => {
    if (user?.id) refreshStatus();
  }, [dispatch, user?.id, router.pathname]);

  useEffect(() => {
    if (!requested || !user?.id) return;
    refreshStatus();
    dispatch({ type: types.SET_PDPA_DIALOG_REQUESTED, payload: false });
  }, [requested, user?.id, dispatch]);

  useEffect(() => {
    setOpenCheckDialog(false);
    setSaving(false);
    submitting.current = false;
    setSaveError(false);
    setStatusError(false);
    setDismissal(null);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || user.pdpaAcceptedAt === undefined) {
      setOpenCheckDialog(false);
      return;
    }
    const outstanding = needsPDPAConsent(user.pdpaAcceptedAt);
    if (!outstanding && user.pdpaAcceptedAt) clearPDPASnooze(user.id);
    setOpenCheckDialog(
      outstanding &&
        !(dismissal?.userId === user.id && dismissal.expiresAt > Date.now()) &&
        !isPDPASnoozed(user.id)
    );
  }, [
    user?.id,
    user?.pdpaAcceptedAt,
    requested,
    router.pathname,
    dismissal,
    storageRevision,
  ]);

  useEffect(() => {
    function onStorage(event) {
      if (
        user?.id &&
        (event.key === snoozeKey(user.id) || event.key === null)
      ) {
        setStorageRevision((value) => value + 1);
        refreshStatus();
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user?.id, dispatch]);

  function closeDialog() {
    setOpenCheckDialog(false);
    try {
      sessionStorage.removeItem("pdpa_dialog_user_id");
    } catch {}
    dispatch({ type: types.SET_PDPA_DIALOG_REQUESTED, payload: false });
  }

  function handleClose() {
    if (user?.id) {
      snoozePDPA(user.id);
      setDismissal({
        userId: user.id,
        expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
      });
    }
    closeDialog();
  }

  async function handleSubmit() {
    if (submitting.current || !user?.id) return;
    const userId = user.id;
    submitting.current = true;
    setSaving(true);
    setSaveError(false);
    const result = await dispatch(updatePDPAStatus());
    if (identity.current !== userId) return;
    submitting.current = false;
    setSaving(false);
    if (
      result?.type === types.FETCH_PDPA_STATUS_SUCCESS &&
      result.payload === true
    ) {
      clearPDPASnooze(userId);
      closeDialog();
    } else {
      setSaveError(true);
    }
  }

  return (
    <DialogContainer
      open={openCheckDialog}
      aria-labelledby="pdpa-dialog-title"
      data-testid="shared-privacy-dialog"
    >
      <BoxContainer
        flexDirection={"column"}
        alignItems={"center"}
        justifyContent={"center"}
        sx={{ width: "100%", height: "100%" }}
      >
        <BoxContainer
          alignItems={"center"}
          gap={"0.625rem"}
          width={"100%"}
          height={"100%"}
          padding={"1.5rem 1.5rem 1rem"}
          borderBottom={"1px solid " + Color.grey}
        >
          <HiredlyIcon />
          <TextStyled
            id="pdpa-dialog-title"
            variant="h5"
            fontSize={"1.25rem"}
            lineHeight={"1.5rem"}
            fontWeight=" 700"
          >
            {t("pdpa.privacy_dialog.title")}
          </TextStyled>
          <CloseButton
            aria-label={t("pdpa.privacy_dialog.close")}
            onClick={handleClose}
            data-testid="shared-privacy-close-btn"
          >
            <CloseIcon />
          </CloseButton>
        </BoxContainer>

        <BoxContainer
          flexDirection={"column"}
          alignItems={"center"}
          padding={isMobile ? "0.5rem 1rem 1.5rem" : "1rem 1.5rem"}
          gap={isMobile ? "0.5rem" : "1rem"}
        >
          <PrivacyIcon />
          <BoxContainer flexDirection={"column"} gap={"1rem"}>
            <TextStyled
              variant="p"
              fontSize={"0.875rem"}
              lineHeight={"1.25rem"}
              fontWeight={"400"}
              color={"rgba(0, 0, 0, 0.6)"}
            >
              {t("pdpa.privacy_dialog.body")}
            </TextStyled>
            <TextStyled
              variant="p"
              fontSize={"0.875rem"}
              lineHeight={"1.25rem"}
              fontWeight={"400"}
              color={"rgba(0, 0, 0, 0.6)"}
            >
              <Trans
                i18nKey="sign_up_form.accept_privacy_terms"
                components={[
                  <LinkStyled key="privacy" href="/privacy-policy" />,
                  <LinkStyled key="terms" href="/terms-and-conditions" />,
                ]}
              />
            </TextStyled>
          </BoxContainer>
        </BoxContainer>

        <BoxContainer
          flexDirection={"column"}
          alignItems={"center"}
          gap={"0.625rem"}
          padding={"1rem 1.875rem"}
          width={"100%"}
          borderTop={"1px solid " + Color.grey}
        >
          <ButtonStyled
            disabled={saving}
            onClick={handleSubmit}
            data-testid="shared-privacy-accept-btn"
          >
            {t(
              saving
                ? "pdpa.privacy_dialog.saving"
                : "pdpa.privacy_dialog.accept"
            )}
          </ButtonStyled>
          {saveError && (
            <TextStyled role="alert">
              {t("pdpa.privacy_dialog.save_error")}
            </TextStyled>
          )}
          {statusError && (
            <TextStyled role="status">
              {t("pdpa.privacy_dialog.status_error")}
            </TextStyled>
          )}
          <LinkStyled href="/privacy-policy" type="important">
            {t("pdpa.privacy_dialog.view_policy")}
          </LinkStyled>
        </BoxContainer>
      </BoxContainer>
    </DialogContainer>
  );
}

export default SharedPrivacyDialog;
